import { useAuth } from "../context/AuthContext";
import React, { useEffect, useState } from "react";
import { IndianRupee, TrendingUp, Calendar, RefreshCw, Search, Download } from "lucide-react";

const TX_PER_PAGE = 20;

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const STATUS_COLORS = {
  Pending:   "bg-amber-50 text-amber-700 border-amber-200",
  Confirmed: "bg-green-50 text-green-700 border-green-200",
  Completed: "bg-blue-50 text-blue-700 border-blue-200",
  Cancelled: "bg-red-50 text-red-700 border-red-200",
  Blocked:   "bg-gray-100 text-gray-600 border-gray-200",
};

const PLATFORM_STYLES = {
  Airbnb:        { bg: "bg-rose-500",   light: "bg-rose-50",   text: "text-rose-700" },
  "Booking.com": { bg: "bg-blue-500",   light: "bg-blue-50",   text: "text-blue-700" },
  Agoda:         { bg: "bg-red-500",    light: "bg-red-50",    text: "text-red-700" },
  MakeMyTrip:    { bg: "bg-indigo-500", light: "bg-indigo-50", text: "text-indigo-700" },
  Website:       { bg: "bg-green-500",  light: "bg-green-50",  text: "text-green-700" },
  Manual:        { bg: "bg-purple-500", light: "bg-purple-50", text: "text-purple-700" },
};

/* ── Simple SVG bar chart ── */
function BarChart({ data }) {
  const max = Math.max(...data.map(d => d.value), 1);
  const H = 140, W = 300;
  const barW = Math.min(48, (W - (data.length + 1) * 8) / data.length);

  return (
    <svg viewBox={`0 0 ${W} ${H + 30}`} className="w-full h-auto">
      {data.map((d, i) => {
        const barH = Math.max(4, (d.value / max) * H);
        const x = 8 + i * (barW + 8);
        const y = H - barH;
        const style = PLATFORM_STYLES[d.label] || PLATFORM_STYLES.Manual;
        return (
          <g key={d.label}>
            <rect x={x} y={y} width={barW} height={barH}
              rx={6}
              className={`fill-current ${style.bg}`}
              style={{ fill: "" }}
            />
            <rect x={x} y={y} width={barW} height={barH}
              rx={6}
              fill={
                d.label === "Airbnb" ? "#f43f5e" :
                d.label === "Booking.com" ? "#3b82f6" :
                d.label === "Agoda" ? "#ef4444" :
                d.label === "MakeMyTrip" ? "#6366f1" :
                d.label === "Website" ? "#22c55e" : "#a855f7"
              }
              opacity={0.85}
            />
            <text x={x + barW / 2} y={H + 16} textAnchor="middle"
              fontSize={9} fill="#9ca3af">
              {d.label.length > 7 ? d.label.slice(0, 7) : d.label}
            </text>
            <text x={x + barW / 2} y={y - 4} textAnchor="middle"
              fontSize={8} fill="#6b7280">
              ₹{(d.value / 1000).toFixed(0)}k
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* ── Donut chart ── */
function DonutChart({ data, total }) {
  const R = 60, cx = 80, cy = 80;
  const circumference = 2 * Math.PI * R;
  let offset = 0;

  const COLORS = ["#f43f5e","#3b82f6","#ef4444","#6366f1","#22c55e","#a855f7"];

  const slices = data.map((d, i) => {
    const pct = total > 0 ? d.value / total : 0;
    const dash = pct * circumference;
    const gap  = circumference - dash;
    const slice = { ...d, dash, gap, offset, color: COLORS[i % COLORS.length], pct };
    offset += dash;
    return slice;
  });

  return (
    <div className="flex items-center gap-6">
      <svg viewBox="0 0 160 160" className="w-32 h-32 flex-shrink-0 -rotate-90">
        {slices.map((s, i) => (
          <circle key={i} cx={cx} cy={cy} r={R}
            fill="none" strokeWidth={20}
            stroke={s.color}
            strokeDasharray={`${s.dash} ${s.gap}`}
            strokeDashoffset={-s.offset}
          />
        ))}
        {/* Center hole */}
        <circle cx={cx} cy={cy} r={50} fill="white" />
      </svg>

      <div className="space-y-2">
        {slices.map(s => (
          <div key={s.label} className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ background: s.color }} />
            <span className="text-xs text-gray-600">{s.label}</span>
            <span className="text-xs font-semibold text-gray-900 ml-auto">
              {Math.round(s.pct * 100)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FinancialsPage() {
  const { authHeaders } = useAuth();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState(new Date().getFullYear() + "-01-01");
  const [to, setTo]     = useState(new Date().getFullYear() + "-12-31");
  const [txSearch, setTxSearch]         = useState("");
  const [txSourceFilter, setTxSourceFilter] = useState("All");
  const [txPage, setTxPage]             = useState(1);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/financials?from=${from}&to=${to}`, { headers: authHeaders() });
      const json = await res.json();
      setData(json.data);
    } catch {}
    setLoading(false);
  }

  async function downloadPdf() {
    setDownloadingPdf(true);
    try {
      const res = await fetch(`${API}/reports/pnl.pdf?from=${from}&to=${to}`, { headers: authHeaders() });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `pnl-report-${from}-to-${to}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {}
    setDownloadingPdf(false);
  }

  useEffect(() => { load(); }, []);

  const byPlatform = data?.byPlatform || {};
  const chartData  = Object.entries(byPlatform).map(([label, d]) => ({
    label, value: d.totalRevenue || 0
  }));

  const totalRevenue  = data?.totalRevenue || 0;
  const totalAdvance  = data?.totalAdvance || 0;
  const totalBalance  = totalRevenue - totalAdvance;
  const totalBookings = data?.totalBookings || 0;
  const totalExpenses = data?.totalExpenses; // null when the expenses feature is off, not just zero
  const netRevenue    = data?.netRevenue;
  const transactions  = data?.transactions || [];

  const filteredTransactions = transactions.filter(t => {
    if (txSourceFilter !== "All" && t.source !== txSourceFilter) return false;
    if (txSearch.trim()) {
      const q = txSearch.trim().toLowerCase();
      if (!t.bookingRef.toLowerCase().includes(q) && !t.guestName.toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const txTotalPages = Math.max(1, Math.ceil(filteredTransactions.length / TX_PER_PAGE));
  const pagedTransactions = filteredTransactions.slice((txPage - 1) * TX_PER_PAGE, txPage * TX_PER_PAGE);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Financials</h1>
          <p className="text-sm text-gray-500 mt-0.5">Revenue overview & payment tracking</p>
        </div>
        <div className="flex items-center gap-3 sm:gap-2">
          <button onClick={downloadPdf} disabled={downloadingPdf}
            className="tap-44 flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 disabled:opacity-50 transition-colors">
            <Download size={14} /> <span className="hidden sm:inline">{downloadingPdf ? "Generating…" : "Download PDF"}</span>
          </button>
          <button onClick={load}
            className="tap-44 p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <RefreshCw size={16} className={`text-gray-500 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Date range filter — flex-wrap so the Apply button can't end up
          clipped off-screen on mobile (two date inputs + labels + button
          don't all fit on one 375px-wide line, and the admin shell clips
          horizontal overflow rather than scrolling it). */}
      <div className="flex items-center gap-3 flex-wrap bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
        <Calendar size={16} className="text-gray-400" />
        <span className="text-sm text-gray-500">From</span>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-1.5 text-sm
            focus:outline-none focus:ring-2 focus:ring-amber-400" />
        <span className="text-sm text-gray-500">To</span>
        <input type="date" value={to} onChange={e => setTo(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-1.5 text-sm
            focus:outline-none focus:ring-2 focus:ring-amber-400" />
        <button onClick={load}
          className="tap-44 px-4 py-1.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500
            text-white text-sm font-medium hover:from-amber-600 hover:to-orange-600
            transition-all shadow-sm">
          Apply
        </button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Revenue",     value: totalRevenue,  prefix: "₹", color: "from-amber-400 to-orange-500" },
          { label: "Advance Collected", value: totalAdvance,  prefix: "₹", color: "from-green-400 to-emerald-500" },
          { label: "Balance Pending",   value: totalBalance,  prefix: "₹", color: "from-rose-400 to-pink-500" },
          { label: "Total Bookings",    value: totalBookings, prefix: "",  color: "from-blue-400 to-indigo-500" },
          ...(totalExpenses !== null && totalExpenses !== undefined ? [
            { label: "Total Expenses",  value: totalExpenses, prefix: "₹", color: "from-red-400 to-rose-500" },
            { label: "Net Revenue",     value: netRevenue,    prefix: "₹", color: "from-teal-400 to-cyan-500" },
          ] : []),
        ].map(k => (
          <div key={k.label}
            className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${k.color}
              flex items-center justify-center mb-3`}>
              <IndianRupee size={14} className="text-white" />
            </div>
            <p className="text-xl font-bold text-gray-900">
              {k.prefix}{k.value.toLocaleString("en-IN")}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">{k.label}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Bar chart */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-1">Revenue by Platform</h3>
          <p className="text-xs text-gray-400 mb-4">Total earnings breakdown</p>
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <RefreshCw size={20} className="animate-spin text-gray-300" />
            </div>
          ) : chartData.length > 0 ? (
            <BarChart data={chartData} />
          ) : (
            <p className="text-sm text-gray-400 text-center py-12">No data yet</p>
          )}
        </div>

        {/* Donut */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-1">Booking Share</h3>
          <p className="text-xs text-gray-400 mb-4">By number of bookings</p>
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <RefreshCw size={20} className="animate-spin text-gray-300" />
            </div>
          ) : chartData.length > 0 ? (
            <DonutChart
              data={Object.entries(byPlatform).map(([label, d]) => ({
                label, value: d.totalBookings || 0
              }))}
              total={totalBookings}
            />
          ) : (
            <p className="text-sm text-gray-400 text-center py-12">No data yet</p>
          )}
        </div>
      </div>

      {/* Platform breakdown table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Platform Breakdown</h3>
        </div>
        <div className="hidden md:block">
          <table className="text-xs">
            <thead>
              <tr className="border-b border-gray-50">
                {["Platform","Bookings","Total Revenue","Advance Paid","Balance","Fully Paid"].map(h => (
                  <th key={h} className="text-left text-[10.5px] font-semibold text-gray-400
                    uppercase tracking-wider px-3 py-2">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {Object.entries(byPlatform).length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-gray-400 text-sm">
                    No financial data for this period
                  </td>
                </tr>
              ) : Object.entries(byPlatform).map(([platform, d]) => {
                const style = PLATFORM_STYLES[platform] || PLATFORM_STYLES.Manual;
                const balance = (d.totalRevenue || 0) - (d.totalAdvance || 0);
                return (
                  <tr key={platform} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1.5 text-[10.5px] font-semibold
                        px-2 py-0.5 rounded-full ${style.light} ${style.text}`}>
                        {platform}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-700">{d.totalBookings}</td>
                    <td className="px-3 py-2 font-semibold text-gray-900">
                      ₹{(d.totalRevenue || 0).toLocaleString("en-IN")}
                    </td>
                    <td className="px-3 py-2 text-green-600">
                      ₹{(d.totalAdvance || 0).toLocaleString("en-IN")}
                    </td>
                    <td className="px-3 py-2 text-rose-600">
                      ₹{balance.toLocaleString("en-IN")}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{d.fullyPaidCount}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="md:hidden divide-y divide-gray-100">
          {Object.entries(byPlatform).length === 0 ? (
            <p className="text-center py-10 text-gray-400 text-sm">No financial data for this period</p>
          ) : (
            Object.entries(byPlatform).map(([platform, d]) => {
              const style = PLATFORM_STYLES[platform] || PLATFORM_STYLES.Manual;
              const balance = (d.totalRevenue || 0) - (d.totalAdvance || 0);
              return (
                <div key={platform} className="p-3.5 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold
                      px-2.5 py-1 rounded-full ${style.light} ${style.text}`}>
                      {platform}
                    </span>
                    <span className="text-xs text-gray-500">{d.totalBookings} booking{d.totalBookings !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <p className="text-gray-400">Revenue</p>
                      <p className="font-semibold text-gray-900">₹{(d.totalRevenue || 0).toLocaleString("en-IN")}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Advance</p>
                      <p className="font-semibold text-green-600">₹{(d.totalAdvance || 0).toLocaleString("en-IN")}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Balance</p>
                      <p className="font-semibold text-rose-600">₹{balance.toLocaleString("en-IN")}</p>
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-400">{d.fullyPaidCount} fully paid</p>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Per-transaction breakdown */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Transactions</h3>
          <p className="text-xs text-gray-400 mt-0.5">Every booking making up the totals above</p>
        </div>
        <div className="flex flex-wrap gap-3 px-5 py-3 border-b border-gray-100">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={txSearch}
              onChange={e => { setTxSearch(e.target.value); setTxPage(1); }}
              placeholder="Search guest or ref…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl
                focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
          <select
            value={txSourceFilter}
            onChange={e => { setTxSourceFilter(e.target.value); setTxPage(1); }}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
          >
            <option value="All">All Platforms</option>
            {["Website", "Airbnb", "Booking.com", "Agoda", "MakeMyTrip", "Manual"].map(s => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="hidden md:block overflow-auto max-h-[65vh]">
          <table className="text-xs">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="border-b border-gray-50">
                {["Ref","Guest","Platform","Check-in","Total","Advance","Balance","Status"].map(h => (
                  <th key={h} className="text-left text-[10.5px] font-semibold text-gray-400
                    uppercase tracking-wider px-3 py-2 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {pagedTransactions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-10 text-gray-400 text-sm">
                    {transactions.length === 0 ? "No transactions for this period" : "No transactions match this filter"}
                  </td>
                </tr>
              ) : pagedTransactions.map(t => {
                const style = PLATFORM_STYLES[t.source] || PLATFORM_STYLES.Manual;
                return (
                  <tr key={t.bookingRef} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-3 py-2 font-mono text-gray-500 whitespace-nowrap">
                      {t.bookingRef}
                    </td>
                    <td className="px-3 py-2 text-gray-900 truncate max-w-[160px]">{t.guestName}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1.5 text-[10.5px] font-semibold
                        px-2 py-0.5 rounded-full ${style.light} ${style.text}`}>
                        {t.source}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                      {new Date(t.checkIn).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                    </td>
                    <td className="px-3 py-2 font-semibold text-gray-900 whitespace-nowrap">
                      ₹{t.totalAmount.toLocaleString("en-IN")}
                      {/* A stay crossing the window's edge is credited only
                          for its nights inside it, so show what the figure
                          is a share OF — otherwise it just looks underpaid. */}
                      {t.periodShare != null && t.periodShare < 0.999 && (
                        <span className="block text-[10px] font-normal text-gray-400">
                          of ₹{(t.fullAmount ?? 0).toLocaleString("en-IN")} · {Math.round(t.periodShare * 100)}% this period
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-green-600 whitespace-nowrap">
                      ₹{t.advancePaid.toLocaleString("en-IN")}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {t.remainingBalance > 0 ? (
                        <span className="text-rose-600 font-medium">₹{t.remainingBalance.toLocaleString("en-IN")}</span>
                      ) : t.totalAmount > 0 ? (
                        <span className="text-green-600">Paid</span>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10.5px] font-medium px-2 py-0.5 rounded-full border
                        ${STATUS_COLORS[t.status] || STATUS_COLORS.Pending}`}>
                        {t.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="md:hidden divide-y divide-gray-100 max-h-[70vh] overflow-auto">
          {pagedTransactions.length === 0 ? (
            <p className="text-center py-10 text-gray-400 text-sm">
              {transactions.length === 0 ? "No transactions for this period" : "No transactions match this filter"}
            </p>
          ) : (
            pagedTransactions.map((t) => {
              const style = PLATFORM_STYLES[t.source] || PLATFORM_STYLES.Manual;
              return (
                <div key={t.bookingRef} className="p-3.5 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 text-sm truncate">{t.guestName}</p>
                      <p className="text-[10.5px] font-mono text-gray-400">{t.bookingRef}</p>
                    </div>
                    <span className={`text-[10.5px] font-medium px-2 py-0.5 rounded-full border whitespace-nowrap flex-shrink-0
                      ${STATUS_COLORS[t.status] || STATUS_COLORS.Pending}`}>
                      {t.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className={`inline-flex items-center gap-1.5 font-semibold px-2 py-0.5 rounded-full ${style.light} ${style.text}`}>
                      {t.source}
                    </span>
                    <span className="text-gray-500">
                      {new Date(t.checkIn).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs border-t border-gray-100 pt-2">
                    <div>
                      <p className="text-gray-400">Total</p>
                      <p className="font-semibold text-gray-900">₹{t.totalAmount.toLocaleString("en-IN")}</p>
                      {t.periodShare != null && t.periodShare < 0.999 && (
                        <p className="text-[10px] text-gray-400">
                          of ₹{(t.fullAmount ?? 0).toLocaleString("en-IN")}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-gray-400">Advance</p>
                      <p className="font-semibold text-green-600">₹{t.advancePaid.toLocaleString("en-IN")}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Balance</p>
                      {t.remainingBalance > 0 ? (
                        <p className="font-semibold text-rose-600">₹{t.remainingBalance.toLocaleString("en-IN")}</p>
                      ) : t.totalAmount > 0 ? (
                        <p className="font-semibold text-green-600">Paid</p>
                      ) : <p className="text-gray-400">—</p>}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        {txTotalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <button onClick={() => setTxPage(p => Math.max(1, p - 1))} disabled={txPage === 1}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
              Previous
            </button>
            <span className="text-sm text-gray-500">Page {txPage} of {txTotalPages}</span>
            <button onClick={() => setTxPage(p => Math.min(txTotalPages, p + 1))} disabled={txPage === txTotalPages}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
